import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Trash2,
  FileText,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  FileDown,
  FileArchive,
  CalendarRange,
  X,
  Copy,
} from "lucide-react";
import { api, API, authStore, openAuthedBlob } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Parse "DD-MM-YYYY", "YYYY-MM-DD" or ISO into a sortable timestamp.
const parseInvoiceDate = (s) => {
  if (!s) return 0;
  const str = String(s).trim();
  const m = str.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (m) {
    const [, d, mo, y] = m;
    const yy = y.length === 2 ? "20" + y : y;
    const t = Date.parse(`${yy}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`);
    if (!Number.isNaN(t)) return t;
  }
  const t = Date.parse(str);
  return Number.isNaN(t) ? 0 : t;
};

// Format an invoice's date + time for the history row.
// Prefers the stored invoice_date (DD-MM-YYYY) + invoice_time (HH:MM),
// falls back to parsing created_at for legacy rows without invoice_time.
const formatDateTime = (inv) => {
  const date = inv.invoice_date || "";
  let time = inv.invoice_time || "";
  if (!time && inv.created_at) {
    try {
      const d = new Date(inv.created_at);
      if (!Number.isNaN(d.getTime())) {
        time = d.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: "Asia/Kolkata",
        });
      }
    } catch {
      /* ignore */
    }
  }
  return { date, time };
};

const SORT_KEYS = {
  invoice_number: (inv) => (inv.invoice_number || "").toLowerCase(),
  invoice_date: (inv) => parseInvoiceDate(inv.invoice_date) || parseInvoiceDate(inv.created_at),
  buyer: (inv) => (inv.buyer?.name || "").toLowerCase(),
  po_number: (inv) => (inv.po_number || "").toLowerCase(),
  grand_total: (inv) => Number(inv.grand_total) || 0,
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function History() {
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkZipping, setBulkZipping] = useState(false);
  const navigate = useNavigate();

  const load = () =>
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(console.error);

  useEffect(() => {
    load();
  }, []);

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Move this invoice to the Recycle Bin? You can restore it within 30 days.")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Moved to Recycle Bin");
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const duplicate = async (inv, e) => {
    e.stopPropagation();
    try {
      // Fetch the full invoice so we get complete line_items & buyer
      const { data: full } = await api.get(`/invoices/${inv.id}`);
      navigate("/manual", {
        state: {
          preset: {
            buyer: full.buyer || {},
            line_items: (full.line_items || []).map((it) => ({
              description: it.description,
              hsn: it.hsn,
              quantity: it.quantity,
              unit: it.unit,
              unit_price: it.unit_price,
              gst_rate: it.gst_rate,
            })),
            notes: full.notes || "",
            sourceInvoiceNumber: full.invoice_number,
          },
        },
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not open duplicate — invoice not found");
    }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "grand_total" || key === "invoice_date" ? "desc" : "asc");
    }
  };

  const exportCsv = async () => {
    const ids = Array.from(selected);
    const params = new URLSearchParams();
    if (ids.length) {
      // Selected rows take priority over the date range filter.
      params.set("ids", ids.join(","));
    } else {
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
    }
    const qs = params.toString();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
    try {
      await openAuthedBlob(`/invoices/export.csv${qs ? "?" + qs : ""}`, {
        filename: `invoices-${stamp}.csv`,
      });
      if (ids.length) {
        toast.success(`Exporting ${ids.length} selected invoice${ids.length === 1 ? "" : "s"}…`);
      } else if (dateFrom || dateTo) {
        toast.success(
          `Exporting invoices${dateFrom ? ` from ${dateFrom}` : ""}${dateTo ? ` to ${dateTo}` : ""}…`,
        );
      } else {
        toast.success("Exporting all invoices as CSV…");
      }
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  const clearDateRange = () => {
    setDateFrom("");
    setDateTo("");
  };
  const setThisMonth = () => {
    setDateFrom(firstOfMonthISO());
    setDateTo(todayISO());
  };

  const downloadSelectedZip = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkZipping(true);
    try {
      // openAuthedBlob only supports GET. Do a raw authed POST + Blob here.
      const token = authStore.getToken();
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      const res = await fetch(`${API}/invoices/bulk-pdf-zip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoices-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(`Downloaded ${ids.length} invoice${ids.length === 1 ? "" : "s"} as ZIP`);
    } catch (err) {
      toast.error("Failed to download ZIP");
    } finally {
      setBulkZipping(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const s = q.trim().toLowerCase();
    const fromTs = dateFrom ? Date.parse(dateFrom + "T00:00:00") : null;
    const toTs = dateTo ? Date.parse(dateTo + "T23:59:59") : null;
    const filtered = invoices.filter((inv) => {
      if (s) {
        const match =
          inv.invoice_number?.toLowerCase().includes(s) ||
          inv.buyer?.name?.toLowerCase().includes(s) ||
          inv.po_number?.toLowerCase().includes(s);
        if (!match) return false;
      }
      if (fromTs !== null || toTs !== null) {
        const when = parseInvoiceDate(inv.invoice_date) || parseInvoiceDate(inv.created_at);
        if (!when) return false;
        if (fromTs !== null && when < fromTs) return false;
        if (toTs !== null && when > toTs) return false;
      }
      return true;
    });
    const keyFn = SORT_KEYS[sortBy] || SORT_KEYS.invoice_date;
    filtered.sort((a, b) => {
      const va = keyFn(a);
      const vb = keyFn(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [invoices, q, sortBy, sortDir, dateFrom, dateTo]);

  const visibleIds = useMemo(() => filteredSorted.map((i) => i.id), [filteredSorted]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selected.has(id));

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => n.delete(id));
      } else {
        visibleIds.forEach((id) => n.add(id));
      }
      return n;
    });
  };

  const toggleRow = (id, e) => {
    e?.stopPropagation?.();
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (
      !window.confirm(
        `Move ${ids.length} invoice${ids.length === 1 ? "" : "s"} to the Recycle Bin? You can restore them within 30 days.`,
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids });
      toast.success(`Moved ${data.deleted} invoice${data.deleted === 1 ? "" : "s"} to Recycle Bin`);
      setSelected(new Set());
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const SortTh = ({ k, label, align = "left", widthClass = "" }) => {
    const active = sortBy === k;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th
        className={`py-3 px-4 font-medium ${widthClass} text-${align} select-none`}
        data-testid={`history-sort-${k}`}
      >
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-zinc-900 transition ${
            active ? "text-zinc-900" : ""
          } ${align === "right" ? "ml-auto" : ""}`}
        >
          <span>{label}</span>
          <Icon size={12} className={active ? "opacity-100" : "opacity-40"} />
        </button>
      </th>
    );
  };

  const hasRange = !!(dateFrom || dateTo);
  const selectedCount = selected.size;

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            History
          </div>
          <h1 className="text-4xl font-bold tracking-tight" data-testid="history-heading">
            Invoice History
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            All invoices generated from Purchase Orders, saved automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Input
            placeholder="Search by invoice #, buyer or PO"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72"
            data-testid="history-search"
          />
          {selectedCount > 0 && (
            <Button
              variant="outline"
              onClick={downloadSelectedZip}
              disabled={bulkZipping}
              data-testid="history-download-zip"
              title={`Download the ${selectedCount} selected invoice${selectedCount === 1 ? "" : "s"} as a ZIP of PDFs`}
            >
              <FileArchive size={14} className="mr-1" />
              {bulkZipping ? "Zipping…" : `Download ZIP (${selectedCount})`}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={filteredSorted.length === 0 && selectedCount === 0}
            data-testid="history-export-csv"
            title={
              selectedCount
                ? `Export the ${selectedCount} selected invoice${selectedCount === 1 ? "" : "s"}`
                : hasRange
                  ? "Export invoices within the chosen date range"
                  : "Export all invoices"
            }
          >
            <FileDown size={14} className="mr-1" />
            {selectedCount
              ? `Export Selected (${selectedCount})`
              : hasRange
                ? "Export Range"
                : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Date range toolbar */}
      <Card className="p-3 mb-4" data-testid="history-range-card">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <CalendarRange size={14} />
            <span className="uppercase tracking-wider">Date Range</span>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="date-from" className="text-xs text-zinc-500">
              From
            </Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-40 text-sm"
              data-testid="history-date-from"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="date-to" className="text-xs text-zinc-500">
              To
            </Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-40 text-sm"
              data-testid="history-date-to"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={setThisMonth}
            data-testid="history-range-this-month"
          >
            This month
          </Button>
          {hasRange && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-zinc-500"
              onClick={clearDateRange}
              data-testid="history-range-clear"
            >
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
          <div className="ml-auto text-xs text-zinc-500">
            Showing <b className="text-zinc-700">{filteredSorted.length}</b> of {invoices.length}
          </div>
        </div>
      </Card>

      {/* Bulk-delete action bar (only when something is ticked) */}
      {selectedCount > 0 && (
        <div
          className="flex items-center justify-between mb-3 px-4 py-2 rounded-md bg-red-50 border border-red-200"
          data-testid="bulk-action-bar"
        >
          <div className="text-sm text-red-800">
            <b data-testid="bulk-selected-count">{selectedCount}</b> invoice
            {selectedCount === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear-selection"
            >
              Clear selection
            </Button>
            <Button
              size="sm"
              className="h-8 bg-red-600 hover:bg-red-700"
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={14} className="mr-1" />
              {bulkDeleting ? "Deleting…" : `Delete ${selectedCount}`}
            </Button>
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500">
            <tr className="text-left">
              <th className="py-3 pl-4 pr-2 w-8">
                <Checkbox
                  checked={
                    allVisibleSelected
                      ? true
                      : someVisibleSelected
                      ? "indeterminate"
                      : false
                  }
                  onCheckedChange={toggleAllVisible}
                  disabled={visibleIds.length === 0}
                  aria-label="Select all visible rows"
                  data-testid="bulk-select-all"
                />
              </th>
              <SortTh k="invoice_number" label="Invoice #" />
              <SortTh k="invoice_date" label="Date" />
              <SortTh k="buyer" label="Buyer" />
              <SortTh k="po_number" label="PO Ref" />
              <SortTh k="grand_total" label="Amount (₹)" align="right" />
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {filteredSorted.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-zinc-400">
                  <FileText size={22} className="inline mb-2" />
                  <div className="text-sm">No invoices found</div>
                </td>
              </tr>
            ) : (
              filteredSorted.map((inv) => {
                const isSelected = selected.has(inv.id);
                return (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoice/${inv.id}`)}
                    className={`border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer ${
                      isSelected ? "bg-red-50/40" : ""
                    }`}
                    data-testid={`history-row-${inv.invoice_number}`}
                  >
                    <td
                      className="py-3 pl-4 pr-2"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleRow(inv.id)}
                        aria-label={`Select ${inv.invoice_number}`}
                        data-testid={`bulk-select-${inv.invoice_number}`}
                      />
                    </td>
                    <td className="py-3 px-4 mono font-medium">{inv.invoice_number}</td>
                    <td className="py-3 px-4 text-zinc-600 whitespace-nowrap">
                      {(() => {
                        const { date, time } = formatDateTime(inv);
                        return (
                          <div className="leading-tight">
                            <div>{date}</div>
                            {time && (
                              <div className="text-[11px] text-zinc-400 mono tabular">{time}</div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 max-w-[280px] truncate">{inv.buyer?.name}</td>
                    <td className="py-3 px-4 mono text-xs text-zinc-500">{inv.po_number}</td>
                    <td className="py-3 px-4 mono tabular text-right">
                      {inv.grand_total?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 flex items-center gap-1 justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await openAuthedBlob(`/invoices/${inv.id}/pdf`);
                          } catch (err) {
                            toast.error("Failed to download PDF");
                          }
                        }}
                        data-testid={`download-${inv.invoice_number}`}
                      >
                        <Download size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-zinc-500 hover:text-blue-600"
                        onClick={(e) => duplicate(inv, e)}
                        title="Duplicate — opens Manual Invoice pre-filled with this buyer & line items"
                        data-testid={`duplicate-${inv.invoice_number}`}
                      >
                        <Copy size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-zinc-400 hover:text-red-600"
                        onClick={(e) => del(inv.id, e)}
                        data-testid={`delete-${inv.invoice_number}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
